/**
 * Consumption side of the `/l/<code>` prefetch started by the inline script in
 * public/index.html.
 *
 * Why this exists: on a cold load the patient form made no network request until the
 * whole bundle had parsed, so the form's fields did not exist when the `load` event
 * fired. Tools that screenshot at `load` — including whatever Twilio's A2P reviewer
 * used on 2026-09-23 — saw a spinner and reported the opt-in form as having no phone
 * number field, which failed Brester Dentistry's campaign with error 30896.
 *
 * The rules here are deliberately strict and fail-open: a miss simply means the caller
 * makes its own request, exactly as it always did. A prefetched response is only ever
 * handed back when it is provably the same request.
 */

/**
 * True when this request carries a procedure/plan scope. The backend returns DIFFERENT
 * teeth for a scoped link, so an unscoped prefetch must never satisfy one.
 * @param {object} [payload]
 */
export function isScopedRequest(payload) {
    const p = payload || {};
    return Boolean(p.aptNum || p.codes || p.txPlan);
}

/**
 * The prefetched `/get-long-url/<urlID>` response, or null.
 *
 * Both UrlRedirect.vue and ViewForm.vue resolve the same short link moments apart, and
 * both are unscoped whenever the prefetch ran at all — so this intentionally serves both
 * rather than being single-use.
 *
 * @param {string} urlID
 * @param {object} [payload] the action payload, checked for scope params
 * @param {object} [win] injectable for tests
 * @returns {Promise<{status:number,data:any}|null>}
 */
export async function takePrefetchedLongUrl(urlID, payload, win) {
    const w = win || (typeof window !== 'undefined' ? window : null);
    const pre = w && w.__teamioPrefetch;
    if (!pre || !pre.longUrl) return null;
    if (!urlID || pre.urlID !== urlID) return null;
    if (isScopedRequest(payload)) return null;

    let hit = null;
    try {
        hit = await pre.longUrl;
    } catch (e) {
        return null;
    }
    // Only a real HTTP response is usable; anything else falls through to axios.
    return hit && typeof hit.status === 'number' ? hit : null;
}

/**
 * The prefetched `/get-form/<formIDForWeb>` response, or null.
 *
 * Single-use: the form config is consumed once by ViewForm. A later form in a packet is
 * a different id and must not be served this one.
 *
 * @param {string} formIDForWeb
 * @param {object} [win] injectable for tests
 * @returns {Promise<{status:number,data:any}|null>}
 */
export async function takePrefetchedForm(formIDForWeb, win) {
    const w = win || (typeof window !== 'undefined' ? window : null);
    const pre = w && w.__teamioPrefetch;
    if (!pre || !pre.form) return null;
    if (!formIDForWeb) return null;

    let hit = null;
    try {
        hit = await pre.form;
    } catch (e) {
        pre.form = null;
        return null;
    }
    pre.form = null; // consume regardless of outcome
    if (!hit || typeof hit.status !== 'number') return null;
    // The prefetch chased whichever id /get-long-url returned. If the caller wants a
    // different form (next form in a packet), this is not it.
    if (hit.formIDForWeb !== formIDForWeb) return null;
    return hit;
}
